// [BD feat] Motion runtime — bd-motion.js
//
// The ONE trigger runtime for every Figma Motion animation in the theme. Copy this file to
// theme/assets/bd-motion.js once per project (it is not per-section) and load it from theme.liquid
// with `defer`. Section CSS owns the @keyframes; this file only decides WHEN they run.
//
// Contract with the section CSS (check-motion.js measures exactly this):
//   <div class="bd-hero__step" data-bd-motion data-bd-motion-trigger="in-view" data-bd-motion-delay="0">
//   .bd-hero__step            { /* pre-state: opacity 0 etc. */ }
//   .bd-motion-in .bd-hero__step,
//   .bd-hero__step.bd-motion-in { animation: bd-hero-step 800ms linear 0ms 1 normal both; }
//   @media (prefers-reduced-motion: reduce) { .bd-hero__step { animation: none; opacity: 1; transform: none; } }
//
// Triggers: in-view (default) | in-view-loop | load | hover | click.
//           hover also covers press (pointerdown — touch has no hover) and keyboard focus.
// Extras:   data-bd-motion-repeat (re-run every time it re-enters), data-bd-motion-threshold="0.4".
//           data-bd-motion-travel: writes --bd-motion-travel (px) = how far the [data-bd-motion-traveller]
//           child can move to sit mirrored at the far edge, kept current on resize — for slides across a box.
(function () {
  'use strict';

  var CLASS = 'bd-motion-in';
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function start(el) { el.classList.add(CLASS); }
  function reset(el) { el.classList.remove(CLASS); }

  // --bd-motion-travel: how far [data-bd-motion-traveller] can move inside el's content box
  var travelObserver = 'ResizeObserver' in window ? new ResizeObserver(function (entries) {
    entries.forEach(function (entry) { measureTravel(entry.target); });
  }) : null;

  function measureTravel(el) {
    var mover = el.querySelector('[data-bd-motion-traveller]');
    if (!mover) return;
    // The mover ends mirrored to where it starts (same inset from the far edge) — a toggle, regardless of
    // any extra end padding the box carries for its text
    var box = el.getBoundingClientRect();
    var inset = mover.getBoundingClientRect().left - box.left - el.clientLeft; // inside the border
    var travel = el.clientWidth - 2 * inset - mover.getBoundingClientRect().width;
    el.style.setProperty('--bd-motion-travel', Math.max(0, travel) + 'px');
  }

  function bind(el) {
    if (el.dataset.bdMotionBound) return;
    el.dataset.bdMotionBound = '1';

    if ('bdMotionTravel' in el.dataset) {
      measureTravel(el);
      if (travelObserver) travelObserver.observe(el);
    }

    var trigger = el.dataset.bdMotionTrigger || 'in-view';

    // Reduced motion: never animate, but ALWAYS render the final state. The CSS media query kills the
    // animation; adding the class here keeps any state-scoped final styling applied.
    if (reduced) { start(el); return; }

    if (trigger === 'load') { start(el); return; }

    if (trigger === 'hover') {
      var again = 'bdMotionRepeat' in el.dataset;
      el.addEventListener('mouseenter', function () { start(el); });
      el.addEventListener('mouseleave', function () { if (again) reset(el); });
      el.addEventListener('pointerdown', function () { start(el); });
      el.addEventListener('pointercancel', function () { if (again) reset(el); });
      el.addEventListener('focusin', function () { start(el); });
      el.addEventListener('focusout', function () { if (again) reset(el); });
      return;
    }

    if (trigger === 'click') {
      el.addEventListener('click', function () { start(el); });
      return;
    }

    // in-view / in-view-loop — the default. An animation that autoplays on load has usually finished
    // before the section is scrolled into view, so the user never sees it.
    if (!('IntersectionObserver' in window)) { start(el); return; }
    var threshold = parseFloat(el.dataset.bdMotionThreshold || '0.25');
    var repeat = 'bdMotionRepeat' in el.dataset;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          start(entry.target);
          if (!repeat) io.unobserve(entry.target);
        } else if (repeat) {
          reset(entry.target);
        }
      });
    }, { threshold: Math.min(Math.max(threshold, 0), 1), rootMargin: '0px 0px -10% 0px' });
    io.observe(el);
  }

  function scan(root) {
    (root || document).querySelectorAll('[data-bd-motion]').forEach(bind);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(); });
  else scan();

  // Shopify theme editor: sections are re-rendered without a page load.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
// [BD feat] Motion runtime
